import { ICellRendererParams } from "ag-grid-community";
import React, { MouseEvent, FC, useRef } from "react";
import styles from "components/portfolio/cellRenderer/ExpandPlanRenderer.module.scss";
import { AxiosInstance } from "axios";
import ArrowPopper from "components/app/ArrowPopper";
import { Skeleton } from "@mui/material";
import moment from "moment";
import { LoadingStatus } from "types/app";
import dynamic from "next/dynamic";
import "moment-timezone";
import { KeyboardArrowDown, OpenInNew } from "@mui/icons-material";
import { AuthDetail } from "types/auth";
import { useState } from "react";
import { useEffect } from "react";
import { Divider } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { ConciergeHistory } from "types/portfolio";
import ConciergeHistoryPop from "components/app/ConciergeHistoryPop";
import ConciergeStatusLabel from "components/app/ConciergeStatusLabel";
import { classHOC } from "components/app/ClassWrapper";

interface ConciergeRendererProps {
  authDetail: AuthDetail;
  axios: AxiosInstance;
  pageId?: string;
}

const useStyles = makeStyles(() => ({
  statusRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginTop: 11,
  },
  row: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginTop: 5,
  },
  label: {
    fontSize: 12,
    color: "#898989",
  },
  status: {
    display: "flex",
    alignItems: "center",
    padding: 3,
    borderRadius: 4,
  },
  expired: {
    fontSize: 12,
    color: "#B2B2B2",
  },
  point: {
    display: "inline-block",
    width: 4,
    height: 4,
    borderRadius: "50%",
    marginRight: 4,
  },
  statusValue: {
    fontSize: 10,
    fontWeight: "bold",
    textTransform: "uppercase",
  },
  value: {
    fontSize: 12,
    color: "#333",
  },
  pointWrapper: {
    height: 16,
    borderRadius: 2,
    display: "flex",
    alignItems: "center",
    justifyContent: "end",
    marginTop: 8,
    fontWeight: 700,
    fontSize: 12,
    textDecoration: "none",
  },
}));

const ConciergeRenderer: FC<ICellRendererParams & ConciergeRendererProps> = ({
  data,
  axios,
  pageId,
  node,
}) => {
  const classes = useStyles();
  const epCellRef = useRef<HTMLDivElement>(null);
  const epPopRef = useRef<HTMLDivElement>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [conciergeData, setConciergeData] = useState<ConciergeHistory[] | undefined>(undefined);
  const [loadingStatus, setLoadingStatus] = useState<LoadingStatus>("notLoaded");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const closePop = (e: Event) => {
      if (
        !epPopRef.current?.contains(e.target as Node) &&
        epPopRef.current !== e.target &&
        !epCellRef.current?.contains(e.target as Node)
      ) {
        setAnchorEl(null);
      }
    };
    document.addEventListener("click", closePop);
    return () => {
      document.removeEventListener("click", closePop);
    };
  }, []);

  const handlePopoverOpen = (event: MouseEvent<HTMLElement>) => {
    if (anchorEl) {
      setAnchorEl(null);
    } else {
      setAnchorEl(event.currentTarget);
      (axios as AxiosInstance)
        .get<ConciergeHistory[]>(
          `/api/dataservice/v1/accounts/${data?.accountId}/concierges/history`
        )
        .then((res) => {
          const data = res.data;
          setConciergeData(data);
          setLoadingStatus("loaded");
        })
        .catch(() => {
          setLoadingStatus("failed");
        });
    }
  };

  const showHistoryList = () => {
    setOpen(true);
    setAnchorEl(null);
  };

  const handleClose = () => {
    setOpen(false);
  };

  if (node.rowPinned) return null;

  return (
    <div className="wrapper">
      {data.conciergeStatus === "Active" ? (
        <>
          <div
            ref={epCellRef}
            onClick={handlePopoverOpen}
            style={{
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
              justifyContent: pageId === "renewals" ? "center" : "inherit",
            }}
          >
            <span style={{ color: "#00823B" }}>Active &nbsp;</span>
            {data?.conciergeEffectiveTo ? (
              <span>({moment(data?.conciergeEffectiveTo).format("MM/DD/YYYY")})</span>
            ) : null}
            <KeyboardArrowDown style={{ fontSize: "1rem" }} />
          </div>
        </>
      ) : data.conciergeStatus === "Expired" ? (
        <div
          ref={epCellRef}
          onClick={handlePopoverOpen}
          style={{
            display: "flex",
            alignItems: "center",
            cursor: "pointer",
            justifyContent: pageId === "renewals" ? "center" : "inherit",
          }}
        >
          <span
            style={{
              color: "#898989",
            }}
          >
            {data.conciergeStatus}
          </span>
          <KeyboardArrowDown style={{ fontSize: "1rem" }} />
        </div>
      ) : data.conciergeStatus === "Not Started" ? (
        <>
          <div
            ref={epCellRef}
            onClick={handlePopoverOpen}
            style={{
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
              justifyContent: pageId === "renewals" ? "center" : "inherit",
            }}
          >
            <span
              style={{
                color: "#FC9D03",
              }}
            >
              {data.conciergeStatus} &nbsp;
            </span>
            {data?.conciergeEffectiveFrom ? (
              <span>({moment(data?.conciergeEffectiveFrom).format("MM/DD/YYYY")})</span>
            ) : null}
            <KeyboardArrowDown style={{ fontSize: "1rem" }} />
          </div>
        </>
      ) : (
        <span
          style={{
            color: "#D4371C",
          }}
        >
          No
        </span>
      )}
      <ArrowPopper
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        placement="left"
        disablePortal={false}
      >
        {data?.conciergeStatus !== "No" ? (
          <div
            ref={epPopRef}
            style={{ overflow: "auto" }}
            className={
              conciergeData
                ? styles.discoveryRecordWrapper
                : `${styles.discoveryRecordWrapper} ${styles.loadingWrapper}`
            }
          >
            {conciergeData?.length && loadingStatus === "loaded" ? (
              <>
                {conciergeData?.slice(0, 3).map((item) => (
                  <div key={item.id}>
                    <div className={classes.statusRow}>
                      <ConciergeStatusLabel type={(item?.status as any) ?? "No"} />
                    </div>
                    <div>
                      <div className={classes.row}>
                        <p
                          className={`${
                            item.status === "Active" ? classes.label : classes.expired
                          }`}
                        >
                          Start Date:{" "}
                        </p>
                        <p
                          className={`${
                            item.status === "Active" ? classes.value : classes.expired
                          }`}
                        >
                          {item?.effectiveFrom
                            ? moment(item?.effectiveFrom).format("MM/DD/YYYY")
                            : "N/A"}
                        </p>
                      </div>
                      <div className={classes.row}>
                        <p
                          className={`${
                            item.status === "Active" ? classes.label : classes.expired
                          }`}
                        >
                          End Date:{" "}
                        </p>
                        <p
                          className={`${
                            item.status === "Active" ? classes.value : classes.expired
                          }`}
                        >
                          {item?.effectiveTo
                            ? moment(item?.effectiveTo).format("MM/DD/YYYY")
                            : "N/A"}
                        </p>
                      </div>
                      <div className={classes.row}>
                        <p
                          className={`${
                            item.status === "Active" ? classes.label : classes.expired
                          }`}
                        >
                          Funded By:{" "}
                        </p>
                        <p
                          className={`${
                            item.status === "Active" ? classes.value : classes.expired
                          }`}
                        >
                          {item?.funded ?? "N/A"}
                        </p>
                      </div>
                    </div>
                    <Divider style={{ marginTop: 12 }} />
                  </div>
                ))}
                <a
                  className={classes.pointWrapper}
                  style={{ display: "flex", alignItems: "center" }}
                  onClick={showHistoryList}
                >
                  <OpenInNew style={{ marginRight: 4, fontSize: "1rem" }} /> Show Details
                </a>
              </>
            ) : loadingStatus !== "failed" ? (
              <div
                className={styles.loadingWrapper}
                style={{ display: "flex", flexDirection: "column" }}
              >
                {Array.from({ length: 4 }).map((x, i) => (
                  <Skeleton
                    key={`${x}${i}`}
                    width={300}
                    height={32}
                    variant="rectangular"
                    style={{ margin: "6px 12px" }}
                  />
                ))}
              </div>
            ) : (
              <div>Sorry, fail to load data</div>
            )}
          </div>
        ) : null}
      </ArrowPopper>
      <ConciergeHistoryPop
        open={open}
        data={conciergeData}
        accountName={data.accountName}
        handleClose={handleClose}
      />
    </div>
  );
};
export default dynamic(() => Promise.resolve(classHOC(ConciergeRenderer)), {
  ssr: false,
});
